import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as fromActions from '@redux/modules/remote-services/remote-support/actions';
import { combineLatestWith, switchMap, tap, withLatestFrom, catchError } from 'rxjs/operators';
import { firstValueFrom, of } from 'rxjs';
import {
  selectCustomerVehicles,
  selectRemoteSupportSessions,
  selectRemoteSupportSessionsCompleted,
  selectRemoteSupportSessionsCustomer,
} from '@redux/modules/remote-services/remote-support';
import { environment } from '@env';
import { Store } from '@ngrx/store';
import {
  DTechRemoteAssistanceCustomerService,
  IMonitorRemoteSupportCustomerService,
} from '@services/api/api.service';
import { RemoteServiceKind, RemoteSupportService } from '@services/api/sdf-remote-services-api.service';
import { AuthService } from '@auth0/auth0-angular';
import { loadedOrganizationsSubscriber, selectSelectedOrganizationId } from '@redux/index';

@Injectable()
export class RemoteSupportEffects {
  private hubConnection: HubConnection;

  setRemoteSupportSessionsFilters$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setRemoteSupportSessionsFilters),
    tap(() => {
      this.store.dispatch(fromActions.setRemoteSupportSessionsPaginator({ paginator: { page: 1, pageSize: 10 } }));
    }),
  ), { dispatch: false });

  getRemoteSupportSessionsList$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getRemoteSupportSessionsList,
      fromActions.setRemoteSupportSessionsPaginator),
    withLatestFrom(
      this.store.select(selectRemoteSupportSessions),
    ),
    tap(() => this.store.dispatch(fromActions.setRemoteSupportSessionsLoading({ isLoading: true }))),
    switchMap(async ([_, vehicles]) => {
      const runningAndActivatedOnly = vehicles.filters.runningAndActivatedOnly === true;
      const listResponse = await firstValueFrom(this.remoteSupportService.getRemoteSupportList(
        runningAndActivatedOnly || vehicles.filters.runningOnly ? true : undefined,
          vehicles.filters.connectedOnly ? vehicles.filters.connectedOnly : undefined,
          vehicles.filters.acceptedConsentOnly ? vehicles.filters.acceptedConsentOnly : undefined,
          runningAndActivatedOnly ? true : undefined,
          vehicles.paginator.page,
          vehicles.paginator.pageSize,
          vehicles.filters.qSearch ? vehicles.filters.qSearch : undefined));
      return [
        fromActions.setRemoteSupportSessionsList({ response: listResponse }),
        fromActions.setRemoteSupportSessionsLoading({ isLoading: false }),
      ];
    }),
    catchError(() => of([
      fromActions.setRemoteSupportSessionsList({ response: { remoteServices: [], total: 0 } }),
      fromActions.setRemoteSupportSessionsLoading({ isLoading: false }),
    ])),
    switchMap((actions) => [...actions]),
  ));

  setRemoteSupportSessionsCompletedFilters$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setRemoteSupportSessionsCompletedFilters),
    tap(() => {
      this.store.dispatch(fromActions.setRemoteSupportSessionsCompletedPaginator({ paginator: { page: 1, pageSize: 10 } }));
    }),
  ), { dispatch: false });

  getRemoteSupportSessionsCompletedList$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getRemoteSupportSessionsCompletedList,
      fromActions.setRemoteSupportSessionsCompletedPaginator),
    withLatestFrom(
      this.store.select(selectRemoteSupportSessionsCompleted),
    ),
    tap(() => this.store.dispatch(fromActions.setRemoteSupportSessionsCompletedLoading({ isLoading: true }))),
    switchMap(async ([_, vehicles]) => {
      const listResponse = await firstValueFrom(this.remoteSupportService.getRemoteSupportList(
        undefined,
        undefined,
        undefined,
        undefined,
          vehicles.paginator.page,
          vehicles.paginator.pageSize,
          vehicles.filters.qSearch));
      return [
        fromActions.setRemoteSupportSessionsCompletedList({ response: listResponse }),
        fromActions.setRemoteSupportSessionsCompletedLoading({ isLoading: false }),
      ];
    }),
    catchError(() => of([
      fromActions.setRemoteSupportSessionsCompletedList({ response: { remoteServices: [], total: 0 } }),
      fromActions.setRemoteSupportSessionsCompletedLoading({ isLoading: false }),
    ])),
    switchMap((actions) => [...actions]),
  ));

  public obtainWsRemoteSupportSessionStatus$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.obtainWsRemoteSupportSessionStatus),
    tap(async (action) => {
      if (this.hubConnection) {
        await this.hubConnection.stop().catch(() => undefined);
      }

      const accessToken = await firstValueFrom(this.auth.getAccessTokenSilently());
      const assistanceId = action.assistanceId;
      this.hubConnection = new HubConnectionBuilder()
        .withUrl(environment.remoteServicesWsURL, {
          accessTokenFactory: () => accessToken,
        })
        .withAutomaticReconnect()
        .build();
      this.hubConnection.on('ReceivedMessage', (connectionStatus, url) => {
        this.store.dispatch(fromActions.setRemoteSupportSessionStatus({ status: connectionStatus }));
        this.store.dispatch(fromActions.setRemoteSupportSessionUrl({ url }));
      });
      this.hubConnection.on('CustomerConsentUpdated', (consent: CustomerConsentUpdatedMessage) => {
        if (consent?.assistanceId?.toLowerCase() !== assistanceId?.toLowerCase()) {
          return;
        }

        this.store.dispatch(fromActions.setRemoteSupportSessionConsent({
          consent: {
            id: consent.consentId,
            remoteServiceId: consent.assistanceId,
            vin: consent.vin,
            status: consent.status,
            expiresAt: consent.expiresAt ?? undefined,
            respondedAt: consent.respondedAt ?? undefined,
            respondedByUserId: consent.respondedByUserId,
          },
        }));
      });
      this.hubConnection.start()
        .then(() => this.hubConnection.invoke('JoinGroup', {
          vin: action.vin,
          assistanceId: action.assistanceId,
          remoteServiceKind: RemoteServiceKind.RemoteSupport,
        }))
        .catch((err) => console.log('error while establishing signalr connection: ' + err));
    }),
  ), { dispatch: false });

  public destroyWsRemoteSupportSessionStatus$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.destroyWsRemoteSupportSessionStatus),
    tap(() => {
      if (this.hubConnection) {
        this.hubConnection.stop()
          .then(() => console.log('SignalR connection stopped'))
          .catch((err) => console.log('error while stopping signalr connection: ' + err));
      }
    }),
  ), { dispatch: false });

  getCustomerVehiclesList$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getCustomerVehiclesList,
      fromActions.setCustomerVehiclesPaginator,
      fromActions.setCustomerVehiclesFilters),
    combineLatestWith(
      this.store.select(selectSelectedOrganizationId),
      loadedOrganizationsSubscriber(this.store),
    ),
    withLatestFrom(
      this.store.select(selectCustomerVehicles),
    ),
    tap(() => this.store.dispatch(fromActions.setCustomerVehiclesLoading({ isLoading: true }))),
    switchMap(async ([[_, selectedOrganizationId], vehicles]) => {
      const vehiclesData = await firstValueFrom(this.dTechRemoteAssistanceCustomerService
        .getVehiclesCustomerList(selectedOrganizationId,
          vehicles.paginator.page,
          vehicles.paginator.pageSize,
          vehicles.filters.qSearch));
      return [
        fromActions.setCustomerVehiclesList({ response: vehiclesData }),
        fromActions.setCustomerVehiclesLoading({ isLoading: false }),
      ];
    }),
    catchError(() => of([
      fromActions.setCustomerVehiclesList({ response: { vehicles: [], total: 0 } }),
      fromActions.setCustomerVehiclesLoading({ isLoading: false }),
    ])),
    switchMap((actions) => [...actions]),
  ));

  getRemoteSupportSessionsCustomerList$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getRemoteSupportSessionsCustomerList,
      fromActions.setRemoteSupportSessionsCustomerPaginator,
      fromActions.setRemoteSupportSessionsCustomerFilters),
    combineLatestWith(
      this.store.select(selectSelectedOrganizationId),
      loadedOrganizationsSubscriber(this.store),
    ),
    withLatestFrom(
      this.store.select(selectRemoteSupportSessionsCustomer),
    ),
    tap(() => this.store.dispatch(fromActions.setRemoteSupportSessionsCustomerLoading({ isLoading: true }))),
    switchMap(async ([[_, selectedOrganizationId], vehicles]) => {
      const vehiclesData = await firstValueFrom(this.remoteSupportCustomerService
        .getIMonitorRemoteSupportCustomerList(selectedOrganizationId,
          vehicles.filters.isCompleted,
          vehicles.paginator.page,
          vehicles.paginator.pageSize,
          vehicles.filters.startDate,
          vehicles.filters.endDate,
          vehicles.filters.qSearch));
      return [
        fromActions.setRemoteSupportSessionsCustomerList({ response: vehiclesData }),
        fromActions.setRemoteSupportSessionsCustomerLoading({ isLoading: false }),
      ];
    }),
    catchError(() => of([
      fromActions.setRemoteSupportSessionsCustomerList({ response: { iMonitorRemoteSupportSessions: [], total: 0 } }),
      fromActions.setRemoteSupportSessionsCustomerLoading({ isLoading: false }),
    ])),
    switchMap((actions) => [...actions]),
  ));


  constructor(
    private actions$: Actions,
    private store: Store,
    private dTechRemoteAssistanceCustomerService: DTechRemoteAssistanceCustomerService,
    private remoteSupportService: RemoteSupportService,
    private remoteSupportCustomerService: IMonitorRemoteSupportCustomerService,
    private auth: AuthService,
  ) {
  }
}

interface CustomerConsentUpdatedMessage {
  consentId: string;
  assistanceId: string;
  vin: string;
  status: string;
  expiresAt?: string | null;
  respondedAt?: string | null;
  respondedByUserId?: string | null;
}
